import React, { useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { usePopper } from 'react-popper'
import { usePortalContainer } from 'src/widget/widget-context'

import styling from './ac-tooltip.module.scss'

export interface IAcTooltip {
  children: React.ReactNode
  label: string
  /** @default 400 */
  delay?: number
  visible?: boolean
}

export const AcTooltip = ({
  children,
  label,
  delay = 400,
  visible = true,
}: IAcTooltip) => {
  const [active, setActive] = useState<boolean>(false)
  const [refElement, setRefElement] = useState<HTMLSpanElement | null>(null)
  const [tooltipRef, setTooltipRef] = useState<HTMLDivElement | null>(null)

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const { styles, attributes } = usePopper(refElement, tooltipRef, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 5],
        },
      },
    ],
  })

  const showTip = () => {
    if (timeoutRef.current) clearInterval(timeoutRef.current)

    timeoutRef.current = setTimeout(() => {
      setActive(true)
    }, delay)
  }

  const hideTip = useCallback(() => {
    clearInterval(timeoutRef.current)
    setActive(false)
  }, [active])

  const portalTarget = usePortalContainer()

  if (!visible) return <>{children}</>

  return (
    <span
      ref={setRefElement}
      onMouseEnter={showTip}
      onMouseLeave={hideTip}>
      {children}
      {active &&
        createPortal(
          <div
            style={styles.popper}
            ref={setTooltipRef}
            className={styling['ac-tooltip']}
            {...attributes.popper}>
            {label}
          </div>,
          portalTarget
        )}
    </span>
  )
}
